import type { Fulfillment } from "@emynor/storefront-sdk";
import type { Branch } from "./types";

export type BranchOrderBlock = "paused" | "closed" | "unavailable" | "global" | null;

export function openAtScheduledTime(branch: Pick<Branch, "timezone" | "weeklyHours">, at: Date): boolean {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: branch.timezone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(at).map(({ type, value }) => [type, value]),
  );
  const day = ["Sat", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri"].indexOf(parts["weekday"] ?? "");
  const minute = Number(parts["hour"]) * 60 + Number(parts["minute"]);
  const toMinute = (value: string) => {
    const [hour, minutePart] = value.slice(0, 5).split(":").map(Number);
    return (hour ?? 0) * 60 + (minutePart ?? 0);
  };
  const period = branch.weeklyHours?.find((row) => row.dayOfWeek === day && !row.closed);
  if (period?.opensAt && period.closesAt) {
    const open = toMinute(period.opensAt);
    const close = toMinute(period.closesAt);
    if (open === close || (open < close && minute >= open && minute < close) || (open > close && minute >= open)) return true;
  }
  const previous = branch.weeklyHours?.find((row) => row.dayOfWeek === (day + 6) % 7 && !row.closed);
  return Boolean(previous?.opensAt && previous.closesAt &&
    toMinute(previous.opensAt) > toMinute(previous.closesAt) && minute < toMinute(previous.closesAt));
}

export function branchOrderBlock(branch: Branch | undefined, fulfillment?: Fulfillment): BranchOrderBlock {
  if (!branch) return "unavailable";
  if (branch.orderPause.active &&
      (!branch.orderPause.resumesAt || Date.now() < Date.parse(branch.orderPause.resumesAt))) return "paused";
  if (fulfillment?.mode === "scheduled") {
    return openAtScheduledTime(branch, new Date(fulfillment.scheduledFor)) ? null : "closed";
  }
  return branch.openNow ? null : "closed";
}

export function nextScheduledPickupTime(branch: Branch, from = new Date()): Date | null {
  const start = Math.ceil((from.getTime() + 15 * 60_000) / (15 * 60_000)) * 15 * 60_000;
  const end = from.getTime() + 7 * 24 * 60 * 60_000;
  for (let at = start; at <= end; at += 15 * 60_000) {
    const candidate = new Date(at);
    if (openAtScheduledTime(branch, candidate)) return candidate;
  }
  return null;
}
